import { EEntityType, EFaceSide, EToolType, ISceneInfo2, ISelected } from '../types';
import editorEvent, { EditorEventName } from '../event/editorEvent';
import EventBus from '../utils/EventBus';
import { Point2dUtils } from '../math/Point2dUtils';
import { EColumnType, IColumnData, IWindowData } from '../data/model/model';
import { getDefaultColumnData } from '../data/model/utils';
import { globalStores } from '.';
import { EContainerType, ETransformType } from '../types/entity';
import type { IFurniture } from '@/editor/data/model/furniture';
import { EAutoLayoutMode } from '../utils/layout/detect';
import { debounce } from 'lodash-es';
import {
  AmbientLight,
  BackSide,
  Color,
  EquirectangularReflectionMapping,
  Fog,
  Group,
  Mesh,
  OrthographicCamera,
  PerspectiveCamera,
  Scene,
  ShaderMaterial,
  SphereGeometry,
  SRGBColorSpace,
  TextureLoader,
  Vector3,
  WebGLRenderer
} from 'three';
import { sceneBgColor } from '../constant/color';
import roomEnvImage from '@/assets/images/roomEnvironment.jpg';
import { freeze } from 'immer';
import { IAssemblyData } from '../data/assembly/assembly';
import { IPoint2d } from '../math/IPoint2d';
import { IMultiModelData } from '../data/multiModel/multiModel';
import { isMultiSelectFurnitureAssemblyOrSepWallOnly } from '../utils/select/multi-select';
import { getMultiModelBoxInfo } from '../data/multiModel/utils';
import { SeparateWallData } from '../data/separateWall/SeparateWall';
import { getSeparateWallBox } from '../data/separateWall/utils';

interface IModeAutoLayoutData {
  allowedDoorIds: string[];
  allowedRoomIds: string[];
  disabledRoomIds: string[];
}

interface IAlignData {
  reference: ISelected | undefined;
  pendingEntities: ISelected[];
  multiBox: { width: number; thickness: number; center: IPoint2d; rotation: number } | null;
}

interface IHoverEntity extends ISelected {
  /**
   * 变换点类型
   */
  transformType?: ETransformType;
}

export interface ITransformingInfo {
  type: EEntityType;
  data: IColumnData | IFurniture | IWindowData | IAssemblyData | IMultiModelData;
}

/**
 * 可订阅的交互状态字段，事件名即字段名
 *
 * 这些字段在画布交互中被高频写入，订阅方（光标、标尺、右键菜单、变换手柄）只关心「值真的变了」，
 * 所以 setter 里统一做相等性判断后再通知。
 */
type TStateChangeEvent = {
  moving: (value: boolean) => void;
  dragging: (value: boolean) => void;
  rotating: (value: boolean) => void;
  alignEnabled: (value: boolean) => void;
  hoverEntity: (value: IHoverEntity) => void;
};

class EditorStateStore {
  private _event = new EventBus<TStateChangeEvent>();

  private _selected: ISelected = { entityId: '', type: EEntityType.EMPTY }; // 当前选中的entity

  private _preSelected: ISelected = { entityId: '', type: EEntityType.EMPTY }; // 上一次选中的entity
  private _columnVirtualData?: IColumnData;
  private _furnitureVirtualData?: IFurniture;
  private _assemblyVirtualData?: IAssemblyData;
  private _windowDoorVirtualData?: IWindowData;
  virtualMultiModelData?: IMultiModelData | null = null;
  /** 旋转/缩放拖拽中的临时数据，仅在拖拽过程中存在，结束/取消时清空 */
  transformingData: ITransformingInfo | null = null;
  private _columnFaceMaterialMap?: Partial<Record<EFaceSide, string>>;
  private _autoLayoutModeData: IModeAutoLayoutData = {
    allowedDoorIds: [],
    allowedRoomIds: [],
    disabledRoomIds: []
  };
  private _alignData: IAlignData = {
    reference: undefined,
    pendingEntities: [],
    multiBox: null
  };
  private _autoLayoutMode: EAutoLayoutMode = EAutoLayoutMode.NONE;

  textInfoContainer: HTMLDivElement;
  rulerContainer: HTMLDivElement;

  /** 是否正在缩放场景。无订阅方，只被 escape 守卫同步读取，所以不走通知 */
  zooming: boolean = false;

  private _moving: boolean = false;
  private _dragging: boolean = false;
  private _rotating: boolean = false;
  private _alignEnabled: boolean = false;
  private _hoverEntity: IHoverEntity = {
    entityId: '',
    type: EEntityType.EMPTY
  };

  /** 是否正在移动物体(目前) */
  get moving() {
    return this._moving;
  }
  set moving(value: boolean) {
    if (this._moving === value) return;
    this._moving = value;
    this._event.emit('moving', value);
  }

  /** 是否正在拖拽场景 */
  get dragging() {
    return this._dragging;
  }
  set dragging(value: boolean) {
    if (this._dragging === value) return;
    this._dragging = value;
    this._event.emit('dragging', value);
  }

  /** 是否正在旋转场景 */
  get rotating() {
    return this._rotating;
  }
  set rotating(value: boolean) {
    if (this._rotating === value) return;
    this._rotating = value;
    this._event.emit('rotating', value);
  }

  /** 是否处于对齐模式下 */
  get alignEnabled() {
    return this._alignEnabled;
  }
  set alignEnabled(value: boolean) {
    if (this._alignEnabled === value) return;
    this._alignEnabled = value;
    this._event.emit('alignEnabled', value);
  }

  /** 当前hover的entity。按引用比较，写入方每次都构造新对象，等价于原先的 observable 语义 */
  get hoverEntity() {
    return this._hoverEntity;
  }
  set hoverEntity(value: IHoverEntity) {
    if (this._hoverEntity === value) return;
    this._hoverEntity = value;
    this._event.emit('hoverEntity', value);
  }

  /** 订阅交互状态字段变化，返回取消订阅函数 */
  onChange<K extends keyof TStateChangeEvent>(key: K, listener: TStateChangeEvent[K]) {
    return this._event.on(key, listener);
  }

  private _sceneInfo: ISceneInfo2;

  // 存放多选临时数据
  private _multiSelectTemporary: IMultiModelData | null = null;

  get multiSelectTemporary() {
    return this._multiSelectTemporary;
  }

  private _textModelFocusData: { focusId?: string } & ISelected = {
    entityId: '',
    type: EEntityType.EMPTY
  };

  constructor() {
    this._sceneInfo = freeze(this.initScene());

    // 初始化text容器
    this.textInfoContainer = document.createElement('div');
    this.textInfoContainer.id = 'text-info-container';
    this.rulerContainer = document.createElement('div');
    this.rulerContainer.id = 'ruler-container';
    this.sceneInfo.canvasContainer.appendChild(this.textInfoContainer);
    this.sceneInfo.canvasContainer.appendChild(this.rulerContainer);
  }

  get sceneInfo() {
    return this._sceneInfo;
  }

  get selected() {
    return this._selected;
  }

  get preSelected() {
    return this._preSelected;
  }

  public get textModelFocusId() {
    if (
      this._textModelFocusData.entityId !== this._selected.entityId ||
      this._textModelFocusData.type !== this._selected.type
    ) {
      this._textModelFocusData = {
        ...this._selected
      };
    }
    return this._textModelFocusData.focusId;
  }

  public setTextModelFocusId(focusId: string) {
    this._textModelFocusData = {
      ...this._selected,
      focusId: focusId
    };
  }

  public resetTextModelFocusData() {
    this._textModelFocusData = {
      ...this._selected
    };
  }

  setVirtualColumnData(data: IColumnData, faceMap?: Partial<Record<EFaceSide, string>>) {
    this._columnVirtualData = data;
    this._columnFaceMaterialMap = faceMap;
  }

  get columnFaceMaterialMap() {
    return this._columnFaceMaterialMap;
  }

  resetColumnVirtualData() {
    this._columnVirtualData = undefined;
    this._columnFaceMaterialMap = undefined;
  }

  set furnitureVirtualData(data: IFurniture | undefined) {
    this._furnitureVirtualData = data;
  }

  get furnitureVirtualData() {
    return this._furnitureVirtualData;
  }

  set assemblyVirtualData(data: IAssemblyData | undefined) {
    this._assemblyVirtualData = data;
  }

  get assemblyVirtualData() {
    return this._assemblyVirtualData;
  }

  set windowDoorVrtualData(data: IWindowData | undefined) {
    this._windowDoorVirtualData = data;
  }

  get windowDoorVrtualData() {
    return this._windowDoorVirtualData;
  }

  get autoLayoutMode() {
    return this._autoLayoutMode;
  }

  setAutoLayoutMode(mode: EAutoLayoutMode) {
    if (this._autoLayoutMode === mode) return;
    this._autoLayoutMode = mode;

    if (mode === EAutoLayoutMode.NONE) {
      this._autoLayoutModeData = {
        allowedDoorIds: [],
        allowedRoomIds: [],
        disabledRoomIds: []
      };
    }
  }

  get autoLayoutModeData() {
    return this._autoLayoutModeData;
  }

  set autoLayoutModeData(data: IModeAutoLayoutData) {
    this._autoLayoutModeData = data;
  }

  get columnVirtualData() {
    let type = EColumnType.squareColumn;
    switch (globalStores.operateInfo.toolType) {
      case EToolType.squareColumn:
        type = EColumnType.squareColumn;
        break;
      case EToolType.cylinder:
        type = EColumnType.cylinder;
        break;
    }
    if (!this._columnVirtualData) {
      this._columnVirtualData = getDefaultColumnData(type);
    } else if (type !== this._columnVirtualData.type) {
      this._columnVirtualData = getDefaultColumnData(type);
    }

    return this._columnVirtualData;
  }

  get alignData() {
    return this._alignData;
  }

  setSelected(selected: ISelected, extraData?: { multiModelData?: IMultiModelData | null }) {
    // 多选数据写入
    if (this._selected.type === EEntityType.MultiModel && selected.type === EEntityType.MultiModel) {
      if (extraData?.multiModelData !== undefined) this._multiSelectTemporary = extraData?.multiModelData;
      return;
    }

    const isSamePoint =
      (!selected.intersectionPoint && !this._selected.intersectionPoint) ||
      (selected.intersectionPoint &&
        this._selected.intersectionPoint &&
        Point2dUtils.equals(selected.intersectionPoint, this._selected.intersectionPoint));

    if (this._selected.entityId === selected.entityId && this._selected.type === selected.type && isSamePoint) return;

    this.setAlignModeEnabled(false);

    this._preSelected = { ...this._selected };
    this._selected = selected;

    // 多选数据处理
    if (this._preSelected.type === EEntityType.MultiModel) {
      this._multiSelectTemporary = null;
    }
    if (this._selected.type === EEntityType.MultiModel) {
      this._multiSelectTemporary = extraData?.multiModelData ?? null;
    }

    editorEvent.emit(EditorEventName.ENTITY_SELECTED, selected);
  }

  clearSelected() {
    this.setSelected({ entityId: '', type: EEntityType.EMPTY });
  }

  setHoverEntity(hoverEntity: IHoverEntity) {
    if (this.hoverEntity.entityId === hoverEntity.entityId) return;

    this.hoverEntity = hoverEntity;
  }

  clearHoverEntity() {
    this.hoverEntity = { entityId: '', type: EEntityType.EMPTY };
  }

  // ========== 对齐相关方法 =========
  setAlignModeEnabled(enable: boolean) {
    if (enable && this.multiSelectTemporary) {
      this.alignEnabled = true;
      const { modelInfos, center, rotation, width, thickness } = this.multiSelectTemporary;
      this._alignData.pendingEntities = modelInfos.map((model) => ({ entityId: model.id, type: model.type }));
      this._alignData.multiBox = { width, thickness, rotation, center };
    } else {
      this.alignEnabled = false;
      this._alignData = {
        reference: undefined,
        pendingEntities: [],
        multiBox: null
      };
    }
  }

  updateAlignReference(entityInfo?: ISelected) {
    this._alignData.reference = entityInfo;
    if (entityInfo) editorEvent.emit(EditorEventName.ALIGN_REFERENCE_SELECTED, entityInfo);
  }

  /** 更新多选数据 */
  refreshFurnitureMultiSelected() {
    const selected = this.selected;
    if (selected.type !== EEntityType.MultiModel) return;
    const multiModelData = this.multiSelectTemporary;
    if (!isMultiSelectFurnitureAssemblyOrSepWallOnly(multiModelData)) return;

    function isEqualGeoProps(model: IFurniture | IAssemblyData, comparativeModel: IFurniture | IAssemblyData) {
      return (
        Point2dUtils.equals(model.center, comparativeModel.center) &&
        model.width === comparativeModel.width &&
        model.thickness === comparativeModel.thickness &&
        model.height === comparativeModel.height &&
        model.rotation === comparativeModel.rotation
      );
    }

    // 隔墙由起止点定义，没有 center/rotation，直接比推导出来的盒——它正是包围盒的输入
    function isEqualSepWallBox(wall: SeparateWallData, comparativeWall: SeparateWallData) {
      const box = getSeparateWallBox(wall);
      const comparativeBox = getSeparateWallBox(comparativeWall);
      return (
        box.x === comparativeBox.x &&
        box.y === comparativeBox.y &&
        box.z === comparativeBox.z &&
        box.width === comparativeBox.width &&
        box.thickness === comparativeBox.thickness &&
        box.height === comparativeBox.height
      );
    }

    const { modelInfos } = multiModelData;
    const nextModelInfos: typeof modelInfos = [];
    let needUpdate = false;

    for (const modelInfo of modelInfos) {
      const { id } = modelInfo;

      if (modelInfo.type === EEntityType.Assembly) {
        const assemblyData = globalStores.assemblyStore.getAssemblyData(id);
        if (!assemblyData) {
          needUpdate = true;
          continue;
        }

        const subModeldatas = assemblyData.accessorieIds
          .map((accessorieId) => globalStores.modelStore.getFurnitureData(accessorieId))
          .filter((furnitureData): furnitureData is IFurniture => Boolean(furnitureData));

        nextModelInfos.push({ id, type: EEntityType.Assembly, data: assemblyData, subModeldatas });
        needUpdate = needUpdate || !isEqualGeoProps(modelInfo.data, assemblyData);
        continue;
      }

      if (modelInfo.type === EEntityType.SeparateWall) {
        // 只有绘制的隔墙可多选，基础隔墙不在其中
        const sepWallData = globalStores.separateWallStore.getSepData(id, false);
        if (!sepWallData) {
          needUpdate = true;
          continue;
        }

        nextModelInfos.push({ id, type: EEntityType.SeparateWall, data: sepWallData, subModeldatas: [] });
        needUpdate = needUpdate || !isEqualSepWallBox(modelInfo.data, sepWallData);
        continue;
      }

      const furnitureData = globalStores.modelStore.getFurnitureData(id);
      if (!furnitureData) {
        needUpdate = true;
        continue;
      }

      nextModelInfos.push({ id, type: EEntityType.Furniture, data: furnitureData, subModeldatas: [] });
      needUpdate = needUpdate || !isEqualGeoProps(modelInfo.data, furnitureData);
    }

    if (needUpdate) {
      const multiBox = getMultiModelBoxInfo(nextModelInfos);
      this.setSelected(selected, {
        multiModelData: { ...multiModelData, ...multiBox, modelInfos: nextModelInfos }
      });
      // 更新对齐包围盒数据
      this.alignData.multiBox = { ...multiBox };
    }
  }

  // ============================= 缩放 =============================
  private _debounceNotZooming = debounce(() => {
    this.zooming = false;
  }, 500);

  setZooming = () => {
    // 只要不是在拖拽场景，就表示是在缩放
    if (this.dragging) return;
    this.zooming = true;
    this._debounceNotZooming();
  };

  // ============================= 场景相关 =============================
  private initScene() {
    const renderer = new WebGLRenderer({ antialias: true });
    const canvasElement = renderer.domElement;
    renderer.outputColorSpace = SRGBColorSpace;
    renderer.setClearColor(sceneBgColor, 1); // 设置背景颜色
    renderer.setPixelRatio(window.devicePixelRatio); // 设置像素比

    // context 恢复时重新设置颜色，不然背景会变黑
    // 虽然 Three.js 理论是要自动恢复这个的，但实际上没有，所以手动恢复一下
    // TODO: 后续可能要找到更好的解决方案
    canvasElement.addEventListener('webglcontextrestored', () => {
      renderer.setClearColor(sceneBgColor, 1); // 设置背景颜色
    });

    const canvasContainer = document.createElement('div');
    canvasContainer.style.width = '100%';
    canvasContainer.style.height = '100%';
    canvasContainer.appendChild(canvasElement);
    return {
      scene2d: this.initScene2dInfo(),
      scene3d: this.initScene3dInfo(),
      renderer,
      canvasElement,
      canvasContainer
    };
  }
  private initScene2dInfo() {
    const size = 10; // m

    const scene = new Scene();
    const intersectableGroup = new Group();
    intersectableGroup.userData.containerType = EContainerType.intersectable;
    scene.add(intersectableGroup);

    const camera = new OrthographicCamera(-size, size, size, -size, 1, 600);
    camera.position.set(0, 0, 500); // 设置相机位置
    camera.lookAt(0, 0, 0); // 设置相机朝向
    camera.layers.enableAll();

    return {
      scene,
      intersectableGroup,
      camera
    };
  }
  private initScene3dInfo() {
    const scene = new Scene();
    const intersectableGroup = new Group();
    intersectableGroup.name = 'intersectableContainer';
    intersectableGroup.userData.intersectableContainer = true;
    scene.add(intersectableGroup);

    const camera = new PerspectiveCamera(75, 1, 0.1, 4000);
    camera.up.set(0, 0, 1);
    camera.position.set(0, -10, 10);
    camera.userData.target = new Vector3(0, 0, 0);

    scene.background = new Color(sceneBgColor);
    scene.fog = new Fog(scene.background, 1, 500);

    // 全局光照
    scene.add(new AmbientLight(0xffffff, 0.3));
    {
      // 环境光设置
      const envTexture = new TextureLoader().load(roomEnvImage);
      envTexture.mapping = EquirectangularReflectionMapping;
      envTexture.colorSpace = SRGBColorSpace;
      // scene.background = envTexture;
      // scene.backgroundRotation.x = Math.PI / 2;
      scene.environment = envTexture;
      scene.environmentRotation.x = Math.PI / 2;
      scene.environmentIntensity = 3.5;
    }

    const vertexShader = `
      varying vec3 vWorldPosition;
			void main() {
				vec4 worldPosition = modelMatrix * vec4( position, 1.0 );
				vWorldPosition = worldPosition.xyz;
				gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
			}`;
    const fragmentShader = `
      uniform vec3 topColor;
			uniform vec3 bottomColor;
			uniform float offset;
			uniform float exponent;
			varying vec3 vWorldPosition;

			void main() {
				float h = normalize( vWorldPosition + offset ).z;
				gl_FragColor = vec4( mix( bottomColor, topColor, max( pow( max( h , 0.0), exponent ), 0.0 ) ), 1.0 );

			}`;
    const uniforms = {
      topColor: { value: new Color(0xcfeeff) },
      bottomColor: { value: new Color(0xfefefe) },
      offset: { value: 0 },
      exponent: { value: 1.5 }
    };
    // uniforms['topColor'].value.copy(hemiLight.color);
    scene.fog.color.copy(uniforms['bottomColor'].value);
    const skyGeo = new SphereGeometry(1000, 32, 15);
    const skyMat = new ShaderMaterial({
      uniforms: uniforms,
      vertexShader: vertexShader,
      fragmentShader: fragmentShader,
      side: BackSide
    });
    const sky = new Mesh(skyGeo, skyMat);
    scene.add(sky);

    return {
      scene,
      intersectableGroup,
      camera: camera as PerspectiveCamera & { userData: { target: Vector3 } }
    };
  }
}

export default EditorStateStore;
